/** Pure helpers for Ferias edición session (media groups + labels). */

export const FERIA_UNGROUPED_ID = "__ungrouped__";

const newGroupId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Sanitize media groups shape `{ id, label, mediaIds }`.
 * @returns {{ ok: true, groups: Array } | { ok: false, error: string }}
 */
export function sanitizeFeriaMediaGroups(input, availablePhotoIds = null) {
  const known =
    availablePhotoIds == null
      ? null
      : new Set(
          (availablePhotoIds || [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id)),
        );
  const seen = new Set();
  const groups = [];
  const list = Array.isArray(input) ? input : [];
  if (list.length > 80) {
    return { ok: false, error: "Demasiados grupos de media" };
  }
  for (let i = 0; i < list.length; i += 1) {
    const g = list[i] || {};
    let id = String(g.id || "")
      .trim()
      .slice(0, 40);
    if (!id || id === FERIA_UNGROUPED_ID) id = newGroupId();
    const label = String(g.label || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 120);
    const mediaIds = [];
    const rawIds = Array.isArray(g.mediaIds) ? g.mediaIds : [];
    for (const raw of rawIds) {
      const mid = Number(raw);
      if (!Number.isFinite(mid) || seen.has(mid)) continue;
      if (known && !known.has(mid)) continue;
      seen.add(mid);
      mediaIds.push(mid);
    }
    if (!mediaIds.length && !label) {
      groups.push({
        id,
        label: `Grupo ${groups.length + 1}`,
        mediaIds: [],
      });
      continue;
    }
    groups.push({ id, label, mediaIds });
  }
  return { ok: true, groups };
}

/** Parse array or JSON string into sanitized groups (drops invalid). */
export function parseFeriaMediaGroups(raw, availablePhotoIds = null) {
  let list = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      list = [];
    }
  }
  const sanitized = sanitizeFeriaMediaGroups(list, availablePhotoIds);
  return sanitized.ok ? sanitized.groups : [];
}

export function ungroupedFeriaPhotoIds(photos = [], groups = []) {
  const used = new Set();
  for (const g of groups || []) {
    for (const id of g.mediaIds || []) used.add(Number(id));
  }
  return (photos || [])
    .map((p) => Number(p.id ?? p))
    .filter((id) => Number.isFinite(id) && !used.has(id));
}

/**
 * Mueve fotos a un grupo con esa etiqueta (reutiliza si ya existe).
 * Así dejan de aparecer en «Materiales» (antes Sin agrupar).
 */
export function assignFeriaPhotosToLabeledGroup(
  mediaGroups = [],
  photoIds = [],
  label = "",
) {
  const title = String(label || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
  const ids = [
    ...new Set(
      (photoIds || [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n) && n > 0),
    ),
  ];
  if (!ids.length) {
    return sanitizeFeriaMediaGroups(mediaGroups).groups;
  }
  const groups = parseFeriaMediaGroups(mediaGroups);
  if (!title) {
    return createFeriaMediaGroup(groups, `Grupo ${groups.length + 1}`, ids);
  }
  const needle = title.toLowerCase();
  const existing = groups.find(
    (g) =>
      String(g.label || "")
        .trim()
        .toLowerCase() === needle,
  );
  if (existing) {
    return moveFeriaPhotosToGroup(groups, ids, existing.id);
  }
  return createFeriaMediaGroup(groups, title, ids);
}

/** Board columns: named groups + trailing «Materiales» (loose). */
export function buildFeriaMediaBoard(photos = [], mediaGroups = []) {
  const byId = new Map();
  for (const p of photos || []) {
    const id = Number(p.id);
    if (Number.isFinite(id)) byId.set(id, p);
  }
  const used = new Set();
  const columns = [];
  for (const g of mediaGroups || []) {
    const gid = String(g?.id || "").trim();
    if (!gid || gid === FERIA_UNGROUPED_ID) continue;
    const items = [];
    for (const raw of g.mediaIds || []) {
      const id = Number(raw);
      if (!Number.isFinite(id) || used.has(id)) continue;
      const photo = byId.get(id);
      if (!photo) continue;
      used.add(id);
      items.push(photo);
    }
    columns.push({
      id: gid,
      label: String(g.label || ""),
      photos: items,
    });
  }
  const ungrouped = (photos || []).filter((p) => !used.has(Number(p.id)));
  columns.push({
    id: FERIA_UNGROUPED_ID,
    label: "",
    photos: ungrouped,
    ungrouped: true,
  });
  return columns;
}

export function mediaGroupsFromFeriaBoard(columns) {
  return (columns || [])
    .filter((c) => c && c.id !== FERIA_UNGROUPED_ID && !c.ungrouped)
    .map((c) => ({
      id: String(c.id),
      label: String(c.label || "").trim(),
      mediaIds: (c.photos || []).map((p) => Number(p.id)),
    }));
}

/** Move selected photo ids into a target group (or ungrouped). */
export function moveFeriaPhotosToGroup(
  mediaGroups = [],
  photoIds = [],
  targetGroupId,
) {
  const ids = [
    ...new Set(
      (photoIds || [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n) && n > 0),
    ),
  ];
  if (!ids.length) {
    return sanitizeFeriaMediaGroups(mediaGroups).groups;
  }
  const idSet = new Set(ids);
  const stripped = (mediaGroups || []).map((g) => ({
    id: String(g.id),
    label: String(g.label || ""),
    mediaIds: (g.mediaIds || [])
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && !idSet.has(n)),
  }));
  const target = String(targetGroupId || "").trim();
  if (!target || target === FERIA_UNGROUPED_ID) {
    return sanitizeFeriaMediaGroups(stripped).groups;
  }
  let found = false;
  const next = stripped.map((g) => {
    if (String(g.id) !== target) return g;
    found = true;
    return { ...g, mediaIds: [...g.mediaIds, ...ids] };
  });
  if (!found) {
    next.push({ id: target, label: "", mediaIds: ids });
  }
  return sanitizeFeriaMediaGroups(next).groups;
}

/** Apply DnD of a photo between board columns. Returns new mediaGroups or null. */
export function applyFeriaBoardDrag(columns, result) {
  const { source, destination, draggableId } = result || {};
  if (!destination) return null;
  if (
    source.droppableId === destination.droppableId &&
    source.index === destination.index
  ) {
    return null;
  }
  const photoId = Number(String(draggableId || "").replace(/^photo-/, ""));
  if (!Number.isFinite(photoId) || photoId <= 0) return null;

  const next = (columns || []).map((c) => ({
    ...c,
    photos: [...(c.photos || [])],
  }));
  const from = next.find((c) => String(c.id) === String(source.droppableId));
  const to = next.find((c) => String(c.id) === String(destination.droppableId));
  if (!from || !to) return null;

  let fromIndex = source.index;
  if (Number(from.photos[fromIndex]?.id) !== photoId) {
    fromIndex = from.photos.findIndex((p) => Number(p.id) === photoId);
  }
  if (fromIndex < 0) return null;
  const [moved] = from.photos.splice(fromIndex, 1);
  if (!moved) return null;

  let insertAt = destination.index;
  if (from.id === to.id && fromIndex < insertAt) insertAt -= 1;
  if (insertAt < 0) insertAt = 0;
  if (insertAt > to.photos.length) insertAt = to.photos.length;
  to.photos.splice(insertAt, 0, moved);

  return mediaGroupsFromFeriaBoard(next);
}

export function createFeriaMediaGroup(
  mediaGroups = [],
  label = "",
  mediaIds = [],
) {
  const ids = (mediaIds || [])
    .map((id) => Number(id))
    .filter((n) => Number.isFinite(n));
  const idSet = new Set(ids);
  const stripped = parseFeriaMediaGroups(mediaGroups).map((g) => ({
    ...g,
    mediaIds: (g.mediaIds || []).filter((n) => !idSet.has(Number(n))),
  }));
  stripped.push({
    id: newGroupId(),
    label: String(label || "").trim() || `Grupo ${stripped.length + 1}`,
    mediaIds: ids,
  });
  return sanitizeFeriaMediaGroups(stripped).groups;
}

/** True if photo has at least one linked Materialoteca entry. */
export function photoHasLinkedMaterial(photo) {
  if (!photo) return false;
  if (
    Array.isArray(photo.linkedMaterialoteca) &&
    photo.linkedMaterialoteca.length
  ) {
    return true;
  }
  const id = Number(
    photo.idMaterialoteca ?? photo.id_Materialoteca ?? photo.IdMaterialoteca,
  );
  return Number.isInteger(id) && id > 0;
}

/** Feria name + año + temporada. */
export function formatFeriaEdicionTitle(edicion) {
  if (!edicion) return "";
  const nombre =
    edicion.feria?.Nombre || edicion.NombreFeria || edicion.Nombre || "";
  const anio = edicion.Anio ? ` ${edicion.Anio}` : "";
  const temporada = edicion.Temporada ? ` ${edicion.Temporada}` : "";
  return `${nombre}${anio}${temporada}`.trim();
}
